/**
 * /me/leaves/my-requests — Session 3d PR 3.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { LeaveRequestItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyLeaveRequestsPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-leave-requests", employeeId],
    queryFn: () => session3dApi.listMyLeaveRequests(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<LeaveRequestItem>[] = [
    {
      headerKey: "me.leave-requests.col.type",
      label: "Type",
      render: (r) => <div className="font-medium">{r.leave_type_name_en}</div>,
    },
    {
      headerKey: "me.leave-requests.col.dates",
      label: "Dates",
      render: (r) => <span dir="ltr" className="font-mono text-xs">{r.start_date} → {r.end_date}</span>,
    },
    {
      headerKey: "me.leave-requests.col.days",
      label: "Days",
      render: (r) => <span className="font-mono">{r.days_requested}</span>,
    },
    {
      headerKey: "me.leave-requests.col.state",
      label: "State",
      render: (r) => <span className="text-xs">{r.state}</span>,
    },
  ];

  return (
    <MeListPage<LeaveRequestItem>
      title={t("me.leave-requests.title", "My Leave Requests")}
      subtitle={t("me.leave-requests.subtitle", "Your personal leave request history with workflow state visibility.")}
      domain="leave-requests"
      khidmatPrefill={t("me.leave-requests.ask_khidmat", "Help me check the status of my pending leave requests.")}
      query={query}
      columns={columns}
      rowKey={(r) => r.request_id}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.leave-requests.title", "My Leave Requests"),
        window_label: t("me.leave-requests.window", "all requests"),
      }}
      emptyHeadline={t("me.leave-requests.empty_headline", "No leave requests yet.")}
      emptyBody={t("me.leave-requests.empty_body", "Khidmat suggests: plan your next leave now to coordinate with your team.")}
    />
  );
}
